import { notifications } from '@mantine/notifications';
import { captureMessage } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

export const FetchAddressVariablesSchema = z.object({
  vat_number: z.string().min(1),
});

export const FetchAddressResponseSchema = z.object({
  city: z.string().nullable(),
  company_name: z.string().nullable(),
  country_code: z.string().nullable(),
  street_box: z.string().nullable(),
  street_name: z.string().nullable(),
  street_number: z.string().nullable(),
  additional_line_1: z.string().optional(),
  additional_line_2: z.string().optional(),
  zip_code: z.string().nullable(),
});

export type FetchAddressVariables = z.infer<typeof FetchAddressVariablesSchema>;
export type FetchAddressResponse = z.infer<typeof FetchAddressResponseSchema>;

async function fetchAddressMutation(
  axiosInstance: Promise<AxiosInstance>,
  vars: FetchAddressVariables,
) {
  const instance = await axiosInstance;

  const { data } = await instance.post('fetch_address', FetchAddressVariablesSchema.parse(vars));

  const safeParse = FetchAddressResponseSchema.safeParse(data);
  if (!safeParse.success) {
    captureMessage(
      'Zod error while parsing fetch address response. Zod error attached to sentry event.',
      { extra: { zodError: safeParse.error } },
    );

    notifications.show({
      title: 'Error',
      message: 'An error occurred while fetching the address. Our team has been notified.',
      color: 'red',
    });

    throw safeParse.error;
  }

  return safeParse.data;
}

export function useFetchAddress() {
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<FetchAddressResponse, ApiError, FetchAddressVariables>({
    mutationFn: (vars: FetchAddressVariables) => fetchAddressMutation(axiosInstance, vars),
  });

  return addActionNameToMutationResult<
    FetchAddressResponse,
    ApiError,
    FetchAddressVariables,
    'fetchAddress'
  >('fetchAddress', mutation);
}
